import { useCallback, useEffect, useRef, useState } from "react";
import { createLogger } from "@/logging";
import { createDatasetDownloadTransport } from "./datasetDownloadTransport";
import { datasetDownloadStore, useDatasetDownloadRequest } from "./datasetDownloadStore";
import { createDirectoryWriter } from "./directoryWriter";
import { totalDownloadBytes, type DownloadEntry } from "./downloadPlan";
import {
  collectDownloadPlan,
  runDownload,
  type DownloadFailure,
  type DownloadProgress,
} from "./downloadRunner";

const logger = createLogger("components/dataset/download");

export type DatasetDownloadPhase = "idle" | "collecting" | "downloading" | "done" | "error";

export type DatasetDownloadState = {
  phase: DatasetDownloadPhase;
  progress: DownloadProgress;
  failures: DownloadFailure[];
  // 같은 relative_path 가 겹쳐 받지 않은 파일. 정상 데이터셋에서는 비어 있다.
  conflicts: DownloadEntry[];
  // 대상 총 바이트(size_bytes 를 모르는 파일은 0으로 센다).
  totalBytes: number;
  cancelled: boolean;
  // 수집 자체가 실패했을 때의 사유.
  errorMessage: string | null;
};

const IDLE: DatasetDownloadState = {
  phase: "idle",
  progress: { total: 0, completed: 0, skipped: 0, failed: 0 },
  failures: [],
  conflicts: [],
  totalBytes: 0,
  cancelled: false,
  errorMessage: null,
};

export type DatasetDownloadController = DatasetDownloadState & {
  active: boolean;
  cancel: () => void;
  retryFailed: () => void;
  dismiss: () => void;
};

/**
 * 대량 다운로드 실행. 스토어의 시작 요청을 받아 수집 → 큐 실행을 진행한다.
 *
 * 재시도는 실패한 entries 만 다시 실행한다(목록 재수집 없음). 이미 받은 파일은 writer 가 크기로
 * 걸러낸다. 그래서 중단 뒤 다시 눌러도 남은 파일만 받는다.
 */
export function useDatasetDownload(datasetUuid: string): DatasetDownloadController {
  const request = useDatasetDownloadRequest(datasetUuid);
  const [state, setState] = useState<DatasetDownloadState>(IDLE);
  const abortRef = useRef<AbortController | null>(null);
  // 실패분 재시도가 목록을 다시 받지 않도록 마지막 실행 컨텍스트를 보관한다.
  const contextRef = useRef<{ directory: FileSystemDirectoryHandle } | null>(null);

  const execute = useCallback(
    async (
      datasetId: string,
      // 이미 데이터셋 폴더까지 연 핸들. 재시도가 같은 폴더를 다시 만들지 않게
      // 호출부가 한 번만 연다.
      directory: FileSystemDirectoryHandle,
      plan: { entries: readonly DownloadEntry[]; conflicts: DownloadEntry[] } | null,
      selection: { prefix: string | null; includeExcluded: boolean } | null,
    ) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      contextRef.current = { directory };
      const transport = createDatasetDownloadTransport(datasetId);

      let entries = plan?.entries ?? [];
      let conflicts = plan?.conflicts ?? [];
      if (plan === null && selection !== null) {
        setState({ ...IDLE, phase: "collecting" });
        try {
          const collected = await collectDownloadPlan(transport, selection, controller.signal);
          entries = collected.entries;
          conflicts = collected.conflicts;
        } catch (error) {
          if (controller.signal.aborted) return;
          logger.error("dataset download collect failed", error);
          setState({
            ...IDLE,
            phase: "error",
            errorMessage: "다운로드 대상을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.",
          });
          return;
        }
      }

      const totalBytes = totalDownloadBytes(entries);
      setState({
        ...IDLE,
        phase: "downloading",
        conflicts,
        totalBytes,
        progress: { total: entries.length, completed: 0, skipped: 0, failed: 0 },
      });

      const outcome = await runDownload(transport, createDirectoryWriter(directory), entries, {
        signal: controller.signal,
        onProgress: (progress) => {
          setState((current) => ({ ...current, progress }));
        },
      });

      if (outcome.failures.length > 0) {
        logger.error("dataset download finished with failures", {
          failed: outcome.failures.length,
          total: outcome.total,
        });
      }
      setState({
        phase: "done",
        progress: {
          total: outcome.total,
          completed: outcome.completed,
          skipped: outcome.skipped,
          failed: outcome.failed,
        },
        failures: outcome.failures,
        conflicts,
        totalBytes,
        cancelled: outcome.cancelled,
        errorMessage: null,
      });
    },
    [],
  );

  // 스토어에 새 요청(nonce)이 오면 실행한다. 요청을 처리한 뒤 지워서 재마운트가 다시 실행하지 않게 한다.
  useEffect(() => {
    if (request === null) return;
    datasetDownloadStore.clear(datasetUuid);
    void request.directory
      .getDirectoryHandle(request.folderName, { create: true })
      .then((target) =>
        execute(datasetUuid, target, null, {
          prefix: request.prefix,
          includeExcluded: request.includeExcluded,
        }),
      )
      .catch((error: unknown) => {
        // 폴더를 만들지 못하면 저장 위치가 없다. 루트에 저장하지 않고 멈춘다.
        logger.error("dataset download folder open failed", error);
        setState({
          ...IDLE,
          phase: "error",
          errorMessage: "저장할 폴더를 만들지 못했습니다. 권한을 확인해 주세요.",
        });
      });
  }, [request, datasetUuid, execute]);

  // 언마운트(페이지 이탈) 시 중단한다. 진행 상태가 페이지 스코프이므로 유지할 방법이 없다.
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    setState((current) => ({ ...current, phase: "done", cancelled: true }));
  }, []);

  const retryFailed = useCallback(() => {
    const directory = contextRef.current?.directory;
    if (directory === undefined || state.failures.length === 0) return;
    void execute(
      datasetUuid,
      directory,
      { entries: state.failures.map((failure) => failure.entry), conflicts: state.conflicts },
      null,
    );
  }, [datasetUuid, execute, state.failures, state.conflicts]);

  const dismiss = useCallback(() => {
    abortRef.current?.abort();
    setState(IDLE);
  }, []);

  return {
    ...state,
    active: state.phase === "collecting" || state.phase === "downloading",
    cancel,
    retryFailed,
    dismiss,
  };
}
